import React from 'react';
import { type IntlShape } from 'react-intl-next';
import ErrorIcon from '@atlaskit/icon/core/migration/status-error--error';
import SuccessIcon from '@atlaskit/icon/core/migration/status-success--check-circle';
// AFP-2532 TODO: Fix automatic suppressions below
// eslint-disable-next-line @atlassian/tangerine/import/entry-points
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

import { type Flag } from '../../../types';
import { CLOUD_ADMIN_URL } from '../../../utils';

import { messages } from '../../i18n';

const getOpenInviteSuccessFlag = (cloudId: string, product: string, intl: IntlShape): Flag => {
	return {
		appearance: 'success',
		icon: (
			<SuccessIcon
				label="success"
				spacing="spacious"
				color={token('color.icon.success', colors.G400)}
				LEGACY_secondaryColor={token('color.icon.inverse', colors.N0)}
			/>
		),
		title: intl.formatMessage(messages.openInviteSuccessFlagTitle, { product }),
		description: intl.formatMessage(messages.openInviteSuccessFlagDescription),
		actions: [
			{
				content: intl.formatMessage(messages.viralSettingsSuccessFlagActionLabel),
				href: `${CLOUD_ADMIN_URL}/s/${cloudId}/signup`,
				target: '_blank',
			},
		],
	};
};

const getDirectAccessSuccessFlag = (
	domains: string[],
	cloudId: string,
	product: string,
	intl: IntlShape,
): Flag => {
	return {
		appearance: 'success',
		icon: (
			<SuccessIcon
				label="success"
				spacing="spacious"
				color={token('color.icon.success', colors.G400)}
				LEGACY_secondaryColor={token('color.icon.inverse', colors.N0)}
			/>
		),
		title: intl.formatMessage(messages.directAccessSuccessFlagTitle, {
			product,
		}),
		description: intl.formatMessage(messages.directAccessSuccessFlagDescription, {
			domains: domains.map((domain) => `@${domain}`).join(', '),
		}),
		actions: [
			{
				content: intl.formatMessage(messages.viralSettingsSuccessFlagActionLabel),
				href: `${CLOUD_ADMIN_URL}/s/${cloudId}/signup`,
				target: '_blank',
			},
		],
	};
};

const getViralSettingsSuccessFlag = (
	domains: string[],
	cloudId: string,
	product: string,
	intl: IntlShape,
): Flag => {
	return {
		appearance: 'success',
		icon: (
			<SuccessIcon
				label="success"
				spacing="spacious"
				color={token('color.icon.success', colors.G400)}
				LEGACY_secondaryColor={token('color.icon.inverse', colors.N0)}
			/>
		),
		title: intl.formatMessage(messages.viralSettingsSuccessFlagTitle, {
			product,
		}),
		description: intl.formatMessage(messages.viralSettingsSuccessFlagDescription, {
			domains: domains.map((domain) => `@${domain}`).join(', '),
		}),
		actions: [
			{
				content: intl.formatMessage(messages.viralSettingsSuccessFlagActionLabel),
				href: `${CLOUD_ADMIN_URL}/s/${cloudId}/signup`,
				target: '_blank',
			},
		],
	};
};

const getOpenInviteFailureFlag = (cloudId: string, product: string, intl: IntlShape): Flag => {
	return {
		appearance: 'error',
		icon: (
			<ErrorIcon
				label="error"
				spacing="spacious"
				color={token('color.icon.danger', colors.R400)}
				LEGACY_secondaryColor={token('color.icon.inverse', colors.N0)}
			/>
		),
		title: intl.formatMessage(messages.openInviteFailureFlagTitle),
		description: intl.formatMessage(messages.openInviteFailureFlagDescription, {
			product,
		}),
		actions: [
			{
				content: intl.formatMessage(messages.viralSettingsFailureFlagActionLabel),
				href: `${CLOUD_ADMIN_URL}/s/${cloudId}/signup`,
				target: '_blank',
			},
		],
	};
};

const getDirectAccessFailureFlag = (
	domain: string,
	cloudId: string,
	product: string,
	intl: IntlShape,
): Flag => {
	return {
		appearance: 'error',
		icon: (
			<ErrorIcon
				label="error"
				spacing="spacious"
				color={token('color.icon.danger', colors.R400)}
				LEGACY_secondaryColor={token('color.icon.inverse', colors.N0)}
			/>
		),
		title: intl.formatMessage(messages.directAccessFailureFlagTitle),
		description: intl.formatMessage(messages.directAccessFailureFlagDescription, {
			domain: `@${domain}`,
			product,
		}),
		actions: [
			{
				content: intl.formatMessage(messages.viralSettingsFailureFlagActionLabel),
				href: `${CLOUD_ADMIN_URL}/s/${cloudId}/signup`,
				target: '_blank',
			},
		],
	};
};

export const getViralSettingsFlags = (
	openInviteEnabled: boolean | undefined,
	directAccessAllDomainsEnabled: boolean | undefined,
	domains: string[],
	cloudId: string,
	product: string,
	intl: IntlShape,
) => {
	let flags: Flag[] = [];
	if (openInviteEnabled && directAccessAllDomainsEnabled) {
		return [getViralSettingsSuccessFlag(domains, cloudId, product, intl)];
	}

	if (openInviteEnabled) {
		flags.push(getOpenInviteSuccessFlag(cloudId, product, intl));
	} else if (openInviteEnabled === false) {
		flags.push(getOpenInviteFailureFlag(cloudId, product, intl));
	}

	if (directAccessAllDomainsEnabled) {
		flags.push(getDirectAccessSuccessFlag(domains, cloudId, product, intl));
	} else if (directAccessAllDomainsEnabled === false) {
		domains.forEach((domain) =>
			flags.push(getDirectAccessFailureFlag(domain, cloudId, product, intl)),
		);
	}

	return flags;
};
